/**
 * Shared utility functions for filter value processing and display
 * Used by CollectionSidebar and CollectionToolbar to maintain consistency
 */

/**
 * Extract the raw value for GraphQL filtering (GID for taxonomyMetafield)
 * @param {string} filterKey - The filter key
 * @param {string} value - The filter value (potentially JSON)
 * @returns {string} The raw value to use for GraphQL filtering
 */
export const extractReadableValue = (filterKey, value) => {
    // This function extracts the VALUE for GraphQL filtering (GID for taxonomyMetafield)
    if (typeof value === 'string' && (value.startsWith('{') || value.startsWith('"{'))) {
        try {
            let parsed = JSON.parse(value);

            // Handle double-encoded JSON
            if (typeof parsed === 'string' && (parsed.startsWith('{') || parsed.startsWith('"{'))) {
                parsed = JSON.parse(parsed);
            }

            // Extract simple value from parsed object for GraphQL filtering
            if (parsed.productMetafield) {
                return parsed.productMetafield.value;
            } else if (parsed.taxonomyMetafield) {
                // For taxonomyMetafield, we need the GID value for GraphQL filtering
                return parsed.taxonomyMetafield.value;
            } else if (parsed.variantOption) {
                return parsed.variantOption.value;
            } else if (parsed.tag) {
                return parsed.tag;
            } else if (parsed.productVendor) {
                return parsed.productVendor;
            } else if (parsed.productType) {
                return parsed.productType;
            } else if (parsed.category) {
            return parsed.category;
            } else if (parsed.price) {
                return `${parsed.price.min}-${parsed.price.max}`;
            } else if (parsed.available !== undefined) {
                return parsed.available ? 'true' : 'false';
            }

            return value; // Fallback to original value
        } catch (e) {
            return value; // Not JSON, return as-is
        }
    }

    return value; // Already a simple value
};

/**
 * Get display label from filter values with support for filter metadata
 * @param {string} filterKey - The filter key
 * @param {string} value - The filter value
 * @param {Array} filters - The filters array from API for label lookup
 * @returns {string} The display label for UI
 */
export const getDisplayLabel = (filterKey, value, filters = []) => {

    let foundFromFilters = null;

    // First, try to find the label from the filters metadata
    filters.forEach(filter => {
        if (filterKey === filter.key) {
            if (filter.values) {
                filter.values.forEach(filterValue => {
                    if (filterValue.input) {
                        try {
                            const parsedInput = JSON.parse(filterValue.input);

                            if (parsedInput.taxonomyMetafield) {
                                if (parsedInput.taxonomyMetafield.value === value || parsedInput.taxonomyMetafield.label === value) {
                                    foundFromFilters = filterValue.label;
                                }
                            }
                        } catch (e) {
                            // Skip invalid JSON
                        }
                    }
                });
            }
        }
    });

    if (foundFromFilters) {
        return foundFromFilters;
    }

    // Fallback to parsing the value directly
    if (typeof value === 'string' && (value.startsWith('{') || value.startsWith('"{'))) {
        try {
            let parsed = JSON.parse(value);

            // Handle double-encoded JSON
            if (typeof parsed === 'string' && (parsed.startsWith('{') || parsed.startsWith('"{'))) {
                parsed = JSON.parse(parsed);
            }

            // Extract display label from parsed object
            if (parsed.productMetafield) {
                return parsed.productMetafield.value;
            } else if (parsed.taxonomyMetafield) {
                // For taxonomyMetafield, use label for display, fallback to value
                return parsed.taxonomyMetafield.label || parsed.taxonomyMetafield.value;
            } else if (parsed.variantOption) {
                return parsed.variantOption.value;
            } else if (parsed.tag) {
                return parsed.tag;
            } else if (parsed.productVendor) {
                return parsed.productVendor;
            } else if (parsed.productType) {
                return parsed.productType;
            } else if (parsed.category) {
                return parsed.category;
            } else if (parsed.price) {
                return `${parsed.price.min}-${parsed.price.max}`;
            } else if (parsed.available !== undefined) {
                return parsed.available ? 'true' : 'false';
            }

            return value; // Fallback to original value
        } catch (e) {
            return value; // Not JSON, return as-is
        }
    }

    return value; // Already a simple value
};

/**
 * Get readable filter value with display formatting
 * @param {string} filterKey - The filter key
 * @param {string} value - The filter value
 * @param {Array} filters - The filters array from API for label lookup
 * @returns {string} The formatted display value
 */
export const getReadableFilterValue = (filterKey, value, filters = []) => {
    // Get display label using the helper function
    let result = getDisplayLabel(filterKey, value, filters);

    // Apply display formatting to the result
    if (filterKey === 'Price' || filterKey === 'price' || filterKey.includes('price')) {
        const stringResult = String(result);
        if (stringResult && stringResult.includes('-')) {
            const [min, max] = stringResult.split('-');
            result = `$${min} - $${max}`;
        } else if (stringResult && !stringResult.startsWith('$')) {
            result = `$${stringResult}`;
        }
    }
    // For availability filters
    else if (filterKey === 'Availability' || filterKey === 'available') {
        if (result === 'true') {
            result = 'In Stock';
        } else if (result === 'false') {
            result = 'Out of Stock';
        }
    }

    return result;
};

/**
 * Create a mapping between GID values and their display labels
 * @param {Array} filters - The filters array from API
 * @returns {Map} Map of GID values to display labels
 */
export const createFilterValueMapping = (filters) => {
    const mapping = new Map();

    if (!filters || !Array.isArray(filters)) return mapping;

    filters.forEach(filter => {
        if (filter.values && Array.isArray(filter.values)) {
            filter.values.forEach(value => {
                // Extract both GID and label from the value
                const gidValue = extractReadableValue(filter.key, value.key);
                const displayLabel = getDisplayLabel(filter.key, value.key, filters);

                // Store the mapping: GID -> Display Label
                if (gidValue !== displayLabel) {
                    mapping.set(gidValue, displayLabel);
                }
            });
        }
    });

    return mapping;
};

/**
 * Get display value for active filters using mapping
 * @param {string} filterKey - The filter key
 * @param {string} value - The filter value
 * @param {Map} filterValueMapping - The mapping of GID to display labels
 * @param {Array} filters - The filters array from API
 * @returns {string} The display value for active filters
 */
export const getDisplayValueForActiveFilter = (filterKey, value, filterValueMapping, filters = []) => {
    // First check if we have a mapping for this GID value
    if (filterValueMapping && filterValueMapping.has(value)) {
        return filterValueMapping.get(value);
    }

    // Fallback to the regular display logic
    return getReadableFilterValue(filterKey, value, filters);
};

/**
 * Get the proper filter value for storage (should be GID for taxonomyMetafield filters)
 * This function determines what value should be stored in the activeFilters object
 * @param {string} filterKey - The filter key
 * @param {string} valueKey - The filter value key (potentially JSON input)
 * @param {Array} filters - The filters array from API for lookup
 * @returns {string} The value to store for GraphQL filtering
 */
export const getFilterValueForStorage = (filterKey, valueKey, filters = []) => {
    // If valueKey is already a JSON input, extract the GID value from it
    if (typeof valueKey === 'string' && (valueKey.startsWith('{') || valueKey.startsWith('"{'))) {
        try {
            let parsed = JSON.parse(valueKey);

            // Handle double-encoded JSON
            if (typeof parsed === 'string' && (parsed.startsWith('{') || parsed.startsWith('"{'))) {
                parsed = JSON.parse(parsed);
            }

            // Extract GID value for storage
            if (parsed.taxonomyMetafield) {
                return parsed.taxonomyMetafield.value; // This is the GID
            } else if (parsed.productMetafield) {
                return parsed.productMetafield.value;
            } else if (parsed.variantOption) {
                return parsed.variantOption.value;
            } else if (parsed.tag) {
                return parsed.tag;
            } else if (parsed.productVendor) {
                return parsed.productVendor;
            } else if (parsed.productType) {
                return parsed.productType;
            } else if (parsed.category) {
                return parsed.category;
            } else if (parsed.price) {
                return `${parsed.price.min}-${parsed.price.max}`;
            } else if (parsed.available !== undefined) {
                return parsed.available ? 'true' : 'false';
            }
        } catch (e) {
            // Not valid JSON, continue to next check
        }
    }

    // Fallback to using valueKey as-is
    return valueKey;
};

/**
 * Get the display label for a stored filter value
 * This reverses the storage process to show human-readable labels
 * @param {string} filterKey - The filter key
 * @param {string} storedValue - The stored filter value (could be GID)
 * @param {Array} filters - The filters array from API for lookup
 * @param {Map} filterValueMapping - Optional mapping of GID to labels
 * @returns {string} The display label
 */
export const getDisplayLabelForStoredValue = (filterKey, storedValue, filters = [], filterValueMapping = new Map()) => {
    // First check if we have a direct mapping
    if (filterValueMapping && filterValueMapping.has(storedValue)) {
        return filterValueMapping.get(storedValue);
    }

    // Search through the API filters to find the label for this GID
    for (const filter of filters) {

        if (filter.key === filterKey || filter.id === filterKey) {
            for (const value of (filter.values || [])) {
                if (value.input) {
                    try {
                        const parsed = JSON.parse(value.input);

                          if (filter.key == 'filter.v.price'){

                           return '$' + parsed.price['min'] + ' - ' + '$' + parsed.price['max']; // Return the display label
                        }

                        if (parsed.taxonomyMetafield && parsed.taxonomyMetafield.value.toLowerCase() === storedValue) {
                            return value.label; // Return the display label
                        }
                        if (parsed.productMetafield && parsed.productMetafield.value.toLowerCase() === storedValue) {
                            return value.label;
                        }
                        if (parsed.variantOption && parsed.variantOption.value.toLowerCase() === storedValue) {
                            return value.label;
                        }
                        if (parsed.category && parsed.category.id.toLowerCase() === storedValue) {
                            return value.label;
                        }
                        // Add other type checks as needed
                    } catch (e) {
                        // Skip invalid JSON
                    }
                }
            }
        }
    }

    // If no mapping found, use the stored value as display
    return storedValue;
};
